import React, { useEffect, useRef, useState } from 'react'

// Hallazgo real del loop de hardening (F5): el backend ya expone
// GET /escriba/pendientes/:consumerID (con canRetry por item, calculado
// server-side) y POST /reintentar desde F1, pero ningún componente los
// llamaba — una consulta que fallaba en STT/IA no tenía NINGÚN rastro
// visible (ni badge, ni lista, ni botón), violando literalmente lo que el
// plan pide en §7.2 paso 5: "nunca fallo silencioso".
const STATUS_FALLBACKS = {
  recording: 'Grabando', uploading: 'Subiendo', uploaded: 'Subida', transcribing: 'Transcribiendo',
  summarizing: 'Generando borrador', draft_ready: 'Borrador listo', approved: 'Aprobado',
  failed: 'Falló', expired: 'Expiró sin revisar', pipeline_crashed: 'Falló (proceso interrumpido)',
  deleted: 'Eliminado',
}

const POLL_INTERVAL_MS = 30000

/**
 * Badge + lista desplegable de grabaciones en curso/con problemas para el
 * paciente actual. Polling simple (mismo criterio que el heartbeat de
 * useEscribaRecorder — no hay websocket) en vez de hooks/useQuery: sigue el
 * mismo patrón "fetch directo" que el resto de useEscribaApi.
 */
const EscribaPending = ({ api, consumerID, t = (k, d) => d }) => {
  const [items, setItems] = useState([])
  const [activeCount, setActiveCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [retryingId, setRetryingId] = useState(null)
  const [error, setError] = useState(null)

  // Bug real (prod, 2026-10-02 — hermano del de EscribaMobile.js del
  // 2026-08-31, que este componente no recibió): el efecto de abajo sólo
  // dependía de `consumerID`, así que capturaba el `api` del PRIMER render
  // — con `getAccessToken()` todavía en `{}` — y como `token()` cierra
  // sobre ESE objeto, los 30s-polls siguientes salían sin Authorization
  // para siempre (40×401 `credentials_required` seguidos en api-main
  // mientras una HCE estuvo abierta). El badge quedaba ciego justo para la
  // grabación que necesitaba mostrar "subiendo / con problema". Dos
  // medidas: (1) no disparar nada hasta `accessTokenReady` (mismo gate que
  // EscribaMobile), y (2) leer `api` vía ref en cada poll, para que una
  // renovación del token durante una sesión larga (getAccessToken refresca
  // cada 5 min) también la vean los polls ya programados.
  const apiRef = useRef(api)
  useEffect(() => { apiRef.current = api }, [api])

  useEffect(() => {
    if (!api.accessTokenReady) return undefined
    let cancelled = false
    const load = () => {
      apiRef.current.getPending(consumerID).then(data => {
        if (cancelled) return
        setItems(data.items || [])
        setActiveCount(data.activeCount || 0)
      }).catch(() => {}) // best-effort: un blip de red no debe hacer parpadear el badge
    }
    load()
    const interval = setInterval(load, POLL_INTERVAL_MS)
    return () => { cancelled = true; clearInterval(interval) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerID, api.accessTokenReady])

  const needsAttention = items.filter(item => item.canRetry)
  // Con un error a la vista no se oculta: el mensaje del 409 tiene que llegar a leerse.
  if (activeCount === 0 && needsAttention.length === 0 && !error) return null

  const statusLabel = status => t(`ehr:escriba_status_${status}`, STATUS_FALLBACKS[status] || status)

  const handleRetry = async id => {
    setRetryingId(id)
    setError(null)
    try {
      await api.retry(id)
      const data = await api.getPending(consumerID)
      setItems(data.items || [])
      setActiveCount(data.activeCount || 0)
    } catch (err) {
      // El back manda { error: código, message: texto para el usuario } (ej. 409
      // not_retryable "No se detectó español…"): se muestra el texto, no el código.
      setError(err.response?.message || err.response?.error || err.message)
      // Con not_retryable la fila ya no es reintentable: se recarga para sacar el botón.
      if (err.response?.error === 'not_retryable') {
        api.getPending(consumerID)
          .then(data => { setItems(data.items || []); setActiveCount(data.activeCount || 0) })
          .catch(() => {})
      }
    } finally {
      setRetryingId(null)
    }
  }

  return (
    <div className="d-inline-block position-relative ms-2">
      <button
        type="button" className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
        data-testid="escriba-pending-toggle" onClick={() => setOpen(o => !o)}
      >
        {activeCount > 0 && <span>{activeCount} {t('ehr:escriba_pending_count', 'en curso')}</span>}
        {needsAttention.length > 0 && (
          <span className="badge bg-danger" data-testid="escriba-pending-badge">
            {needsAttention.length} {t('ehr:escriba_needs_attention', 'con problema')}
          </span>
        )}
      </button>
      {open && (
        <div className="dropdown-menu show p-2" style={{ minWidth: 300 }} data-testid="escriba-pending-list">
          {error && <div className="alert alert-danger py-1 px-2 small">{error}</div>}
          {items.length === 0 && (
            <div className="small text-secondary px-1">{t('ehr:escriba_pending_empty', 'Sin grabaciones pendientes')}</div>
          )}
          {items.map(item => {
            const shortId = item.id.split('/')[1]
            return (
              <div
                key={item.id} data-testid={`escriba-pending-item-${shortId}`}
                className="d-flex justify-content-between align-items-center py-1 small border-bottom"
              >
                <span>
                  {statusLabel(item.status)}
                  {item.isPartial && <span className="text-warning"> · {t('ehr:escriba_partial', 'parcial')}</span>}
                </span>
                {item.canRetry && (
                  <button
                    type="button" className="btn btn-sm btn-link" disabled={retryingId === item.id}
                    data-testid={`escriba-pending-retry-${shortId}`} onClick={() => handleRetry(item.id)}
                  >
                    {retryingId === item.id ? t('ehr:escriba_retrying', 'Reintentando…') : t('ehr:escriba_retry', 'Reintentar')}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default EscribaPending
